/**
 * Instagram Testimonials 2025 - JavaScript Components
 * Modern, optimized, and feature-rich
 */

// ==================================================
// InternalVideo Component (Same as Facebook but optimized)
// ==================================================
class InternalVideoIG extends HTMLElement {
  constructor() {
    super();

    this.playButton = this.querySelector(".ig-video__play");
    this.loaded = false;
    this.video = this.querySelector("video");
    
    if (!this.video) return;

    this.invalidFormatSrc = this.video.querySelector("source[data-invalid]");
    if (this.invalidFormatSrc) {
      this.invalidFormatSrc.remove();
    }

    // Event Listeners
    if (this.playButton) {
      this.playButton.addEventListener("click", this.togglePlay.bind(this));
    }

    if (this.video) {
      this.video.addEventListener("ended", this.onEnded.bind(this));
      this.video.addEventListener("loadstart", this.onLoadStart.bind(this));
      this.video.addEventListener("canplaythrough", this.onCanPlay.bind(this));
      this.video.addEventListener("play", this.onCanPlay.bind(this));
    }

    // Intersection Observer for lazy loading
    if (
      this.dataset.actionOnInactive === "pause" &&
      "IntersectionObserver" in window
    ) {
      const options = {
        root: null,
        rootMargin: "0px",
        threshold: 0.05,
      };
      this.observer = new IntersectionObserver(
        this.handleIntersection.bind(this),
        options
      );
      this.observer.observe(this);
    }
  }

  togglePlay() {
    if (!this.video) return;

    if (this.video.paused) {
      if (!this.loaded) {
        this.video.load();
        this.loaded = true;
      }

      this.classList.add("internal-video--playing");
      
      const playPromise = this.video.play();

      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            this.classList.add("internal-video--playing");
          })
          .catch((error) => {
            console.warn("Video play failed:", error);
            this.classList.remove("internal-video--playing");
          });
      }
    } else {
      this.video.pause();
      this.classList.remove("internal-video--playing");
    }
  }

  onEnded() {
    this.classList.remove("internal-video--playing");
  }

  onCanPlay() {
    this.classList.remove("internal-video--loading");
  }

  onLoadStart() {
    this.classList.add("internal-video--loading");
  }

  handleIntersection(entries) {
    if (!this.video) return;

    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        // Load video sources
        for (let source of this.video.querySelectorAll("source[data-src]")) {
          source.setAttribute("src", source.getAttribute("data-src"));
          source.removeAttribute("data-src");
        }

        this.video.load();

        const playPromise = this.video.play();

        if (playPromise !== undefined) {
          playPromise.catch((error) => {
            if (error.name === "NotAllowedError") {
              this.classList.remove("internal-video--playing");
            }
          });
        }

        if (this.observer) {
          this.observer.disconnect();
        }
      }
    });
  }
}

// Register the custom element (only if not already registered)
if (!customElements.get("internal-video")) {
  customElements.define("internal-video", InternalVideoIG);
}

// ==================================================
// Instagram Action Buttons Interactions
// ==================================================
class InstagramActions {
  constructor() {
    this.init();
  }

  init() {
    // Like button animation
    document.addEventListener("click", (e) => {
      if (e.target.closest(".ig-action-btn")) {
        const btn = e.target.closest(".ig-action-btn");
        this.animateButton(btn);
      }

      // Comment like button
      if (e.target.closest(".ig-comment__like")) {
        const btn = e.target.closest(".ig-comment__like");
        this.toggleCommentLike(btn);
      }
    });

    // Double tap to like (mobile)
    let lastTap = 0;
    document.addEventListener("touchend", (e) => {
      const now = Date.now();
      const timeSince = now - lastTap;

      if (timeSince < 300 && timeSince > 0) {
        const media = e.target.closest(".ig-post__media");
        if (media) {
          this.showLikeAnimation(media);
        }
      }

      lastTap = now;
    });
  }

  animateButton(btn) {
    btn.style.transform = "scale(1.2)";
    setTimeout(() => {
      btn.style.transform = "";
    }, 150);
  }

  toggleCommentLike(btn) {
    const svg = btn.querySelector("svg");
    const path = svg.querySelector("path");
    
    if (btn.classList.contains("liked")) {
      btn.classList.remove("liked");
      path.setAttribute("fill", "none");
      path.setAttribute("stroke", "currentColor");
    } else {
      btn.classList.add("liked");
      path.setAttribute("fill", "#ed4956");
      path.setAttribute("stroke", "#ed4956");
      this.animateButton(btn);
    }
  }

  showLikeAnimation(media) {
    // Create heart animation
    const heart = document.createElement("div");
    heart.innerHTML = `
      <svg width="100" height="100" viewBox="0 0 24 24" style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%) scale(0); fill: #fff; opacity: 0; pointer-events: none; transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);">
        <path d="M16.792 3.904A4.989 4.989 0 0121.5 9.122c0 3.072-2.652 4.959-5.197 7.222-2.512 2.243-3.865 3.469-4.303 3.752-.477-.309-2.143-1.823-4.303-3.752C5.141 14.072 2.5 12.167 2.5 9.122a4.989 4.989 0 014.708-5.218 4.21 4.21 0 013.675 1.941c.84 1.175.98 1.763 1.12 1.763s.278-.588 1.11-1.766a4.17 4.17 0 013.679-1.938z"/>
      </svg>
    `;
    
    media.appendChild(heart);
    const svg = heart.querySelector("svg");

    // Animate
    requestAnimationFrame(() => {
      svg.style.transform = "translate(-50%, -50%) scale(1)";
      svg.style.opacity = "1";
    });

    setTimeout(() => {
      svg.style.transform = "translate(-50%, -50%) scale(1.2)";
      svg.style.opacity = "0";
    }, 300);

    setTimeout(() => {
      heart.remove();
    }, 600);
  }
}

// Initialize Instagram Actions
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    new InstagramActions();
  });
} else {
  new InstagramActions();
}

// ==================================================
// Splide Component (Same as Facebook)
// ==================================================
class SplideComponentIG extends HTMLElement {
  connectedCallback() {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => this.init());
    } else {
      this.init();
    }
  }

  init() {
    const splideElement = this.querySelector(".splide");
    if (!splideElement) {
      console.warn("Splide element not found");
      return;
    }

    // Wait for Splide library to load
    if (typeof Splide === "undefined") {
      console.warn("Splide library not loaded. Retrying...");
      setTimeout(() => this.init(), 100);
      return;
    }

    try {
      const settings = {
        type: this.dataset.type || "slide",
        autoplay: this.dataset.autoplay === "true",
        interval: (parseInt(this.dataset.autoplaySpeed) || 5) * 1000,
        perPage: parseInt(this.dataset.slidesDesktop) || 3,
        perMove: parseInt(this.dataset.perMoveDesktop) || 1,
        gap: (parseInt(this.dataset.gapDesktop) || 24) + "px",
        padding: {
          left: parseInt(this.dataset.sidePaddingDesktop) || 0,
          right: parseInt(this.dataset.sidePaddingDesktop) || 0,
        },
        arrows: !splideElement.classList.contains("splide--destroy-desktop"),
        pagination: !splideElement.classList.contains("splide--destroy-desktop"),
        pauseOnHover: true,
        pauseOnFocus: true,
        resetProgress: false,
        breakpoints: {
          768: {
            perPage: parseInt(this.dataset.slidesMobile) || 1,
            gap: (parseInt(this.dataset.gapMobile) || 15) + "px",
            padding: {
              left: parseInt(this.dataset.sidePaddingMobile) || 15,
              right: parseInt(this.dataset.sidePaddingMobile) || 15,
            },
            destroy: this.dataset.destroyMobile === "true",
          },
          1024: {
            destroy: this.dataset.destroyDesktop === "true",
          },
        },
      };

      // Padding calculation
      if (this.dataset.paddingCalcDesktop === "true") {
        settings.focus = 0;
        settings.omitEnd = true;
      }

      // Initialize Splide
      const splide = new Splide(splideElement, settings);

      // Pause videos when slide changes
      if (this.dataset.pauseVideos === "true") {
        splide.on("move", () => {
          this.querySelectorAll("internal-video").forEach((video) => {
            const videoElement = video.querySelector("video");
            if (videoElement && !videoElement.paused) {
              videoElement.pause();
              video.classList.remove("internal-video--playing");
            }
          });
        });
      }

      // Mount the slider
      splide.mount();

      // Store instance
      this.splide = splide;

      console.log("Instagram Splide initialized successfully");
    } catch (error) {
      console.error("Error initializing Splide:", error);
    }
  }

  disconnectedCallback() {
    if (this.splide) {
      try {
        this.splide.destroy();
      } catch (error) {
        console.warn("Error destroying Splide:", error);
      }
    }
  }
}

// Register the custom element (only if not already registered)
if (!customElements.get("splide-component") && !customElements.get("splide-component-ig")) {
  customElements.define("splide-component", SplideComponentIG);
} else if (!customElements.get("splide-component-ig")) {
  customElements.define("splide-component-ig", SplideComponentIG);
}

// ==================================================
// Copy Button Component
// ==================================================
class CopyButtonIG extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("click", this.copyToClipboard.bind(this));
  }

  copyToClipboard() {
    const content = this.dataset.content;

    if (!content) {
      console.warn("No content to copy");
      return;
    }

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(content)
        .then(() => {
          this.showSuccess();
        })
        .catch((err) => {
          console.error("Failed to copy:", err);
          this.fallbackCopy(content);
        });
    } else {
      this.fallbackCopy(content);
    }
  }

  fallbackCopy(content) {
    const textArea = document.createElement("textarea");
    textArea.value = content;
    textArea.style.position = "fixed";
    textArea.style.left = "-999999px";
    textArea.style.top = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand("copy");
      if (successful) {
        this.showSuccess();
      }
    } catch (err) {
      console.error("Fallback copy failed:", err);
    }

    document.body.removeChild(textArea);
  }

  showSuccess() {
    this.dataset.success = "true";

    const copyText = this.querySelector(".copy-text");
    const copySuccess = this.querySelector(".copy-success");

    if (copyText) copyText.style.display = "none";
    if (copySuccess) copySuccess.style.display = "block";

    setTimeout(() => {
      this.dataset.success = "false";
      if (copyText) copyText.style.display = "block";
      if (copySuccess) copySuccess.style.display = "none";
    }, 2000);
  }
}

// Register the custom element (only if not already registered)
if (!customElements.get("copy-button") && !customElements.get("copy-button-ig")) {
  customElements.define("copy-button", CopyButtonIG);
} else if (!customElements.get("copy-button-ig")) {
  customElements.define("copy-button-ig", CopyButtonIG);
}

// ==================================================
// Instagram-specific Features
// ==================================================

// Story ring animation
function animateStoryRings() {
  const storyAvatars = document.querySelectorAll(".ig-post__avatar--story");
  storyAvatars.forEach((avatar) => {
    // Already animated via CSS
  });
}

// Lazy load images
function lazyLoadImages() {
  if ("IntersectionObserver" in window) {
    const imageObserver = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const img = entry.target;
            if (img.dataset.src) {
              img.src = img.dataset.src;
              img.removeAttribute("data-src");
              img.classList.add("loaded");
            }
            observer.unobserve(img);
          }
        });
      },
      {
        rootMargin: "50px 0px",
        threshold: 0.01,
      }
    );

    document.querySelectorAll("img[data-src]").forEach((img) => {
      imageObserver.observe(img);
    });
  }
}

// Animate sections on scroll
function initScrollAnimations() {
  if ("IntersectionObserver" in window) {
    const animateObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove("animate--hidden");
            animateObserver.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: "0px 0px -50px 0px",
      }
    );

    document.querySelectorAll(".animate-section").forEach((section) => {
      animateObserver.observe(section);
    });
  }
}

// Initialize all features
function initInstagramTestimonials() {
  animateStoryRings();
  lazyLoadImages();
  initScrollAnimations();
  console.log("Instagram Testimonials 2025 loaded successfully! 🚀");
}

// Run on DOM ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initInstagramTestimonials);
} else {
  initInstagramTestimonials();
}

// Export for potential module use
if (typeof module !== "undefined" && module.exports) {
  module.exports = {
    InternalVideoIG,
    InstagramActions,
    SplideComponentIG,
    CopyButtonIG,
  };
}

